/**
 * PostNotificationsPage - Display post notifications (likes, comments, mentions)
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, query, where, orderBy, limit, getDocs, doc, updateDoc } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { useAuth } from '@/contexts/AuthContext';
import { PostNotification } from '@/types';
import { Heart, MessageCircle, UserPlus, ArrowLeft, Check } from 'lucide-react';
import { toast } from 'sonner';

export default function PostNotificationsPage() {
  const navigate = useNavigate();
  const { user, firebaseUser } = useAuth();
  const [notifications, setNotifications] = useState<PostNotification[]>([]);
  const [loading, setLoading] = useState(true);
  const [markingRead, setMarkingRead] = useState<string | null>(null);

  useEffect(() => {
    loadNotifications();
  }, [firebaseUser, user]);

  const loadNotifications = async () => {
    if (!firebaseUser || !user?.franchiseId) return;

    try {
      setLoading(true);

      const notificationsRef = collection(db, 'post_notifications');
      const q = query(
        notificationsRef,
        where('userId', '==', firebaseUser.uid),
        where('franchiseId', '==', user.franchiseId),
        orderBy('createdAt', 'desc'),
        limit(50)
      );

      const snapshot = await getDocs(q);
      const notificationsData = snapshot.docs.map((doc) => ({
        notificationId: doc.id,
        ...doc.data(),
      })) as PostNotification[];

      setNotifications(notificationsData);
    } catch (error) {
      console.error('Error loading notifications:', error);
      toast.error('Error al cargar las notificaciones');
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (notificationId: string) => {
    try {
      setMarkingRead(notificationId);

      await updateDoc(doc(db, 'post_notifications', notificationId), {
        isRead: true,
        readAt: new Date(),
      });

      // Update local state
      setNotifications((prev) =>
        prev.map((n) =>
          n.notificationId === notificationId ? { ...n, isRead: true } : n
        )
      );
    } catch (error) {
      console.error('Error marking notification as read:', error);
      toast.error('Error al marcar como leída');
    } finally {
      setMarkingRead(null);
    }
  };

  const handleNotificationClick = async (notification: PostNotification) => {
    // Mark as read if unread
    if (!notification.isRead) {
      await markAsRead(notification.notificationId);
    }

    // Navigate to post
    navigate(`/feed?postId=${notification.postId}`);
  };

  const markAllAsRead = async () => {
    const unreadNotifications = notifications.filter((n) => !n.isRead);

    if (unreadNotifications.length === 0) {
      toast.info('No hay notificaciones sin leer');
      return;
    }

    try {
      // Update all unread notifications
      await Promise.all(
        unreadNotifications.map((n) =>
          updateDoc(doc(db, 'post_notifications', n.notificationId), {
            isRead: true,
            readAt: new Date(),
          })
        )
      );

      // Update local state
      setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));

      toast.success('Todas las notificaciones marcadas como leídas');
    } catch (error) {
      console.error('Error marking all as read:', error);
      toast.error('Error al marcar todas como leídas');
    }
  };

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'post_like':
        return <Heart className="w-5 h-5 text-danger" />;
      case 'post_comment':
        return <MessageCircle className="w-5 h-5 text-info" />;
      case 'post_mention':
        return <UserPlus className="w-5 h-5 text-success" />;
      default:
        return <Heart className="w-5 h-5 text-ink-subtle" />;
    }
  };

  const formatTimeAgo = (timestamp: any) => {
    if (!timestamp || !timestamp.toDate) return 'Hace un momento';

    const date = timestamp.toDate();
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Ahora';
    if (diffMins < 60) return `Hace ${diffMins}min`;
    if (diffHours < 24) return `Hace ${diffHours}h`;
    if (diffDays < 7) return `Hace ${diffDays}d`;

    return date.toLocaleDateString('es-ES', { day: 'numeric', month: 'short' });
  };

  const unreadCount = notifications.filter((n) => !n.isRead).length;

  if (loading) {
    return (
      <div className="min-h-screen bg-surface-2 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-info/30 mx-auto mb-4"></div>
          <p className="text-ink-muted">Cargando notificaciones...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-surface-2 py-8">
      <div className="max-w-3xl mx-auto px-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/feed')}
              className="text-ink-muted hover:text-ink dark:hover:text-white transition"
            >
              <ArrowLeft className="w-6 h-6" />
            </button>
            <div>
              <h1 className="text-3xl font-bold text-ink">
                Notificaciones
              </h1>
              {unreadCount > 0 && (
                <p className="text-sm text-ink-muted mt-1">
                  {unreadCount} {unreadCount === 1 ? 'notificación nueva' : 'notificaciones nuevas'}
                </p>
              )}
            </div>
          </div>

          {/* Mark all as read button */}
          {unreadCount > 0 && (
            <button
              onClick={markAllAsRead}
              className="flex items-center gap-2 px-4 py-2 text-sm bg-info text-white rounded-lg hover:bg-info/90 transition"
            >
              <Check className="w-4 h-4" />
              <span>Marcar todas</span>
            </button>
          )}
        </div>

        {/* Notifications List */}
        {notifications.length === 0 ? (
          <div className="bg-surface rounded-lg shadow-sm p-12 text-center">
            <p className="text-ink-muted text-lg mb-2">
              No tienes notificaciones
            </p>
            <p className="text-ink-subtle text-ink-muted text-sm">
              Cuando alguien le dé like o comente tus posts, verás las notificaciones aquí
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {notifications.map((notification) => (
              <button
                key={notification.notificationId}
                onClick={() => handleNotificationClick(notification)}
                disabled={markingRead === notification.notificationId}
                className={`w-full flex items-start gap-4 p-4 rounded-lg transition ${
                  notification.isRead
                    ? 'bg-surface hover:bg-surface-2'
                    : 'bg-info/10 hover:bg-info/20'
                } ${
                  markingRead === notification.notificationId
                    ? 'opacity-50 cursor-not-allowed'
                    : ''
                }`}
              >
                {/* User photo */}
                <div className="flex-shrink-0">
                  {notification.triggeredByPhoto ? (
                    <div className="relative">
                      <img
                        src={notification.triggeredByPhoto}
                        alt={notification.triggeredByName}
                        className="w-12 h-12 rounded-full object-cover"
                      />
                      <div className="absolute -bottom-1 -right-1 bg-surface rounded-full p-1">
                        {getNotificationIcon(notification.type)}
                      </div>
                    </div>
                  ) : (
                    <div className="relative">
                      <div className="w-12 h-12 rounded-full bg-info/10 flex items-center justify-center text-info font-bold">
                        {notification.triggeredByName.charAt(0).toUpperCase()}
                      </div>
                      <div className="absolute -bottom-1 -right-1 bg-surface rounded-full p-1">
                        {getNotificationIcon(notification.type)}
                      </div>
                    </div>
                  )}
                </div>

                {/* Content */}
                <div className="flex-1 text-left">
                  <p className="text-ink text-sm">
                    <span className="font-semibold">{notification.triggeredByName}</span>{' '}
                    {notification.type === 'post_like' && 'le dio like a tu post'}
                    {notification.type === 'post_comment' && 'comentó tu post'}
                    {notification.type === 'post_mention' && 'te mencionó en un post'}
                  </p>
                  {notification.body && (
                    <p className="text-ink-muted text-sm mt-1 line-clamp-2">
                      {notification.body}
                    </p>
                  )}
                  <p className="text-ink-subtle text-ink-muted text-xs mt-1">
                    {formatTimeAgo(notification.createdAt)}
                  </p>
                </div>

                {/* Post image thumbnail */}
                {notification.imageUrl && (
                  <div className="flex-shrink-0">
                    <img
                      src={notification.imageUrl}
                      alt="Post"
                      className="w-16 h-16 rounded object-cover"
                    />
                  </div>
                )}

                {/* Unread indicator */}
                {!notification.isRead && (
                  <div className="flex-shrink-0">
                    <div className="w-2 h-2 bg-info rounded-full"></div>
                  </div>
                )}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
